/**
 * Money, in one place, for a currency that is not assumed to have cents.
 *
 * Every money column in this product is an integer number of *minor units* of
 * the workspace's currency, and how many minor units make one major unit is a
 * property of the currency rather than a constant. It is 100 for the dollar and
 * the pound, 0 for the yen and the won, and 1000 for the dinar. Code that
 * divides by a hundred is therefore correct in most of the world and wrong —
 * by a factor of a hundred, or of ten — in the rest of it. A ¥2,500 invoice
 * shown as "¥25.00", or a KD 1.250 payment typed in and saved as 125 fils,
 * is not a formatting bug; it is the wrong amount of somebody's money.
 *
 * The scale is asked of the platform rather than kept in a table here, so a
 * currency this file has never heard of is still handled correctly, and a
 * revaluation is the browser's problem rather than a patch to the product.
 */

/** Cache: resolving a currency's scale costs an Intl construction. */
const scales = new Map<string, number>();

/**
 * How many decimal places this currency is written with.
 *
 * Asked of `Intl`, which carries the ISO 4217 minor-unit table, so this knows
 * that the yen has none and the Kuwaiti dinar has three without anybody
 * maintaining a list. An unrecognised code falls back to two, which is the
 * commonest case and is what the old hardcoded behaviour did everywhere.
 *
 * @param currency the ISO 4217 code, for example "USD" or "JPY"
 * @returns the number of fraction digits, 0 or more
 */
export function fractionDigits(currency: string): number {
  const cached = scales.get(currency);
  if (cached !== undefined) {
    return cached;
  }
  let digits = 2;
  try {
    const resolved = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).resolvedOptions().maximumFractionDigits;
    if (typeof resolved === 'number' && Number.isInteger(resolved) && resolved >= 0) {
      digits = resolved;
    }
  } catch {
    digits = 2;
  }
  scales.set(currency, digits);
  return digits;
}

/**
 * How many minor units make one major unit of this currency.
 *
 * @param currency the ISO 4217 code
 * @returns 1, 100, 1000 and so on
 */
export function minorUnitsPerMajor(currency: string): number {
  return 10 ** fractionDigits(currency);
}

/**
 * Money, for display, from integer minor units.
 *
 * Display only. Every figure the server stores and compares is an integer
 * number of minor units; this scales it for a human and nothing downstream
 * ever reads the result back.
 *
 * @param minorUnits the amount in minor units
 * @param currency the ISO 4217 code the amount is denominated in
 * @returns the formatted amount, in the reader's own locale
 */
export function formatMinorUnits(minorUnits: number, currency: string): string {
  const major = minorUnits / minorUnitsPerMajor(currency);
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(major);
  } catch {
    return `${major.toFixed(fractionDigits(currency))} ${currency}`;
  }
}

/**
 * Turns minor units back into the plain decimal a form field shows.
 *
 * Not localised, unlike {@link formatMinorUnits}: this is what goes *into* an
 * input that {@link parseMinorUnits} will read back, so it has to be in the
 * same plain form that parser accepts.
 *
 * @param minorUnits the amount
 * @param currency the ISO 4217 code
 * @returns for example "149.00", "2500", or "1.250"
 */
export function minorUnitsToInput(minorUnits: number, currency: string): string {
  const digits = fractionDigits(currency);
  const negative = minorUnits < 0;
  const absolute = Math.abs(Math.trunc(minorUnits))
    .toString()
    .padStart(digits + 1, '0');
  const whole = digits === 0 ? absolute : absolute.slice(0, absolute.length - digits);
  const fraction = digits === 0 ? '' : `.${absolute.slice(absolute.length - digits)}`;
  return `${negative ? '-' : ''}${whole}${fraction}`;
}

/**
 * Parses a typed amount into integer minor units for a given currency.
 *
 * Returns null for anything that is not a plain amount the currency can
 * actually represent, so a mistyped figure is refused at the field rather than
 * silently rounded into somebody's ledger. What "can represent" means depends
 * on the currency: "80.5" is eight thousand and fifty cents in dollars, five
 * hundred and eighty-odd nothing in yen — where it is simply refused, because
 * there is no such thing as half a yen — and 80,500 fils in dinars.
 *
 * Built from the digit strings rather than by multiplying a float, so no
 * amount is ever one minor unit out from a rounding step nobody asked for.
 *
 * @param typed what the user typed
 * @param currency the ISO 4217 code the field is denominated in
 * @returns the amount in minor units, or null when it is not one
 */
export function parseMinorUnits(typed: string, currency: string): number | null {
  const digits = fractionDigits(currency);
  const trimmed = typed.trim().replace(/[,$\s]/g, '');
  const pattern =
    digits === 0 ? /^\d+$/ : new RegExp(String.raw`^\d+(\.\d{1,${String(digits)}})?$`);
  if (!pattern.test(trimmed)) {
    return null;
  }
  const [major, fraction = ''] = trimmed.split('.');
  const amount = Number(major) * 10 ** digits + Number(fraction.padEnd(digits, '0') || '0');
  return Number.isSafeInteger(amount) ? amount : null;
}

/**
 * How a field should explain that it could not read what was typed.
 *
 * Phrased from the currency rather than from a constant, because "at most two
 * decimal places" is wrong advice in Tokyo.
 *
 * @param currency the ISO 4217 code
 * @returns the message to put under the field
 */
export function amountHint(currency: string): string {
  const digits = fractionDigits(currency);
  if (digits === 0) {
    return `Enter a whole amount; ${currency} has no decimal places.`;
  }
  return `Enter an amount with at most ${digits === 2 ? 'two' : String(digits)} decimal places.`;
}
